import type { InfoSheetContent } from '@uiKit/components/InfoSheet.vue'
import { PROPERTY_STATUS, type PropertyStatus } from './propertyStatus'
import type { OfferingFigures } from '../types/propertyTypes'

// ⓘ explainers + tile values for the Properties list cards — copy, formulas
// and show/hide rules mirror the mobile app byte-for-byte. Pure builders over
// the parsed card figures, so every rule is unit-tested
// (__tests__/offeringInfo.spec.ts).

/** One figure tile on a card: value, caption, and the explainer (if any). */
export interface OfferingInfoTile {
  value?: string
  caption?: string
  /** Green caption with a percent icon — the Sliceal discount. */
  isDiscountCaption?: boolean
  /** Omitted → no ⓘ icon on the tile. */
  sheet?: InfoSheetContent
}

const PERCENT_BASE = 100
const MIN_DISCOUNT_PCT = 1
// A net yield this far below zero is rounding noise and shows as 0.00%.
const NET_YIELD_TOLERANCE = -0.005
// Float nudge so a value stored just under a tenth isn't floored a step down.
const FLOOR_EPSILON = 1e-9

/** Floors to one decimal, nudged so 9.1 (stored as 9.0999…) stays 9.1. */
export const floor1dp = (value: number): number => Math.floor(value * 10 + FLOOR_EPSILON) / 10

/** null / non-finite / ≤ 0 → null, else "8.00%". */
export const formatPct = (value: number | null | undefined): string | null =>
  value == null || !Number.isFinite(value) || value <= 0 ? null : `${value.toFixed(2)}%`

const aedFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 })
const formatAed = (amount: number): string => `AED ${aedFormatter.format(amount)}`

const isPositive = (value: number | null | undefined): value is number =>
  value != null && Number.isFinite(value) && value > 0

// ── Sliceal Price (Live / Launching Soon) ────────────────────────────────

/** The Sliceal price is the purchase price — no fallback to the listing price. */
const slicealPriceOf = (figures: OfferingFigures): string | undefined => figures.purchasePrice

export const buildSlicealPriceSheet = (figures: OfferingFigures): InfoSheetContent | undefined => {
  const { marketValue, currentMarketValue, discountPct } = figures
  const slicealPrice = slicealPriceOf(figures)
  if (discountPct == null || !Number.isFinite(discountPct)) return undefined
  if (discountPct < MIN_DISCOUNT_PCT || discountPct >= PERCENT_BASE) return undefined
  if (!slicealPrice || !marketValue || !isPositive(currentMarketValue)) return undefined

  const saving = Math.round((currentMarketValue * discountPct) / PERCENT_BASE)
  const paid = currentMarketValue - saving
  if (paid <= 0) return undefined
  const uplift = floor1dp((saving / paid) * PERCENT_BASE)
  const discount = floor1dp(discountPct)

  return {
    title: 'Sliceal Price',
    note: 'This is the property price listed on Sliceal.',
    description: `This property is listed on Sliceal at ${slicealPrice}, against an independent market valuation of ${marketValue}. You therefore acquire your share at ${discount}% below the current market value — equivalent to an instant ${uplift}% gain on the amount you invest. This uplift is based on the property's latest independent valuation and is unrealized until you sell.`,
    rows: [
      { label: 'Market price', value: marketValue },
      { operator: '−', label: 'Sliceal price', value: `− ${slicealPrice}` },
      {
        operator: '=',
        label: `You save · ${discount}% off`,
        value: formatAed(saving),
        isResult: true,
      },
    ],
  }
}

export const buildSlicealPriceTile = (figures: OfferingFigures): OfferingInfoTile => {
  const { discountPct } = figures
  const caption =
    discountPct != null && Number.isFinite(discountPct) && discountPct >= MIN_DISCOUNT_PCT
      ? `${floor1dp(discountPct)}% off`
      : undefined
  return {
    value: slicealPriceOf(figures),
    caption,
    isDiscountCaption: Boolean(caption),
    sheet: buildSlicealPriceSheet(figures),
  }
}

// ── Market Price (all cards) ─────────────────────────────────────────────

export const MARKET_PRICE_SHEET: InfoSheetContent = {
  title: 'Market Price',
  note: "The property's current value on external markets as per DLD valuation",
  description:
    'An independent estimate of what this property would sell for on the open Dubai market today, based on the latest DLD-certified valuation and recent comparable sales in the area.',
}

export const buildMarketPriceTile = (figures: OfferingFigures): OfferingInfoTile => ({
  value: figures.marketValue,
  caption: 'Current Valuation',
  sheet: MARKET_PRICE_SHEET,
})

// ── Listing Price (Fully Funded; tile label "LISTED PRICE") ──────────────

export const LISTING_PRICE_SHEET: InfoSheetContent = {
  title: 'Listing Price',
  note: "This is the property's market price at the time of initial offering as per DLD valuation",
  description:
    'An independent estimate of what this property would have sold for on the open Dubai market when it was originally listed, based on the DLD-certified valuation and comparable sales in the area.',
}

export const buildListingPriceTile = (figures: OfferingFigures): OfferingInfoTile => ({
  value: figures.listingPrice,
  caption: 'At Launch',
  sheet: LISTING_PRICE_SHEET,
})

// ── Gross Yield (all cards) ──────────────────────────────────────────────

export const buildGrossYieldSheet = (figures: OfferingFigures): InfoSheetContent | undefined => {
  const { grossYieldPct, fundingTotal, annualRentalIncome } = figures
  const grossYield = formatPct(grossYieldPct)
  if (!grossYield || grossYieldPct == null) return undefined
  const price = slicealPriceOf(figures)
  if (!price) return undefined
  const rent =
    annualRentalIncome ??
    (isPositive(fundingTotal) ? Math.round((fundingTotal * grossYieldPct) / PERCENT_BASE) : 0)
  if (!(rent > 0)) return undefined

  return {
    title: 'Gross Yield',
    note: 'This is your estimated annual rental yield before expenses',
    description:
      "Estimated average rental yield of the unit over 1 year holding period. It shows the annual rental income as a percentage of the property's price - before any costs are deducted.",
    rows: [
      { label: 'Annual rental income', value: formatAed(rent) },
      { operator: '÷', label: 'Sliceal price', value: price },
      { operator: '×', label: '', value: '100' },
      { operator: '=', label: 'Gross yield', value: grossYield, isResult: true },
    ],
  }
}

/** Undefined when the yield is null / ≤ 0 — the tile is hidden. */
export const buildGrossYieldTile = (figures: OfferingFigures): OfferingInfoTile | undefined => {
  const value = formatPct(figures.grossYieldPct)
  if (!value) return undefined
  return { value, caption: 'Annual Rent', sheet: buildGrossYieldSheet(figures) }
}

// ── Estimated ROI (all cards) ────────────────────────────────────────────

export const buildEstimatedRoiSheet = (figures: OfferingFigures): InfoSheetContent | undefined => {
  const { annualRoiPct, appreciationPct } = figures
  if (!isPositive(annualRoiPct) || !isPositive(appreciationPct)) return undefined
  const netYield = annualRoiPct - appreciationPct
  if (netYield < NET_YIELD_TOLERANCE) return undefined

  return {
    title: 'Estimated ROI',
    note: 'The total annual return you receive from this property, expressed as a percentage of your investment',
    description:
      "It combines the property's net rental yield with its estimated annual appreciation. This projection is based on the historical DLD valuation trend of the property. These figures are estimates, not guarantees.",
    rows: [
      { label: 'Net Yield', value: `${Math.max(netYield, 0).toFixed(2)}%` },
      {
        operator: '+',
        label: 'Estimated Appreciation',
        value: `${appreciationPct.toFixed(2)}%`,
      },
      {
        operator: '=',
        label: 'Estimated ROI',
        value: `${annualRoiPct.toFixed(2)}%`,
        isResult: true,
      },
    ],
  }
}

export const buildEstimatedRoiTile = (figures: OfferingFigures): OfferingInfoTile => ({
  value: formatPct(figures.annualRoiPct) ?? undefined,
  caption: 'Annually',
  sheet: buildEstimatedRoiSheet(figures),
})

// ── Card figure block ────────────────────────────────────────────────────

export type OfferingTileId = 'price' | 'market' | 'grossYield' | 'estimatedRoi'

export interface OfferingCardTile {
  id: OfferingTileId
  label: string
  tile: OfferingInfoTile
}

/**
 * Figures block per tab, in display order.
 *   Live / Launching Soon: SLICEAL PRICE · MARKET PRICE · GROSS YIELD · ESTIMATED ROI
 *   Fully Funded:          LISTED PRICE  · MARKET PRICE · GROSS YIELD · ESTIMATED ROI
 * Gross Yield drops out when the yield is null / ≤ 0.
 */
export const buildOfferingCardTiles = (
  figures: OfferingFigures,
  status: PropertyStatus,
): OfferingCardTile[] => {
  const isFullyFunded = status === PROPERTY_STATUS.FULLY_FUNDED
  const grossYield = buildGrossYieldTile(figures)
  const tiles: (OfferingCardTile | undefined)[] = [
    isFullyFunded
      ? { id: 'price', label: 'Listed Price', tile: buildListingPriceTile(figures) }
      : { id: 'price', label: 'Sliceal Price', tile: buildSlicealPriceTile(figures) },
    { id: 'market', label: 'Market Price', tile: buildMarketPriceTile(figures) },
    grossYield ? { id: 'grossYield', label: 'Gross Yield', tile: grossYield } : undefined,
    { id: 'estimatedRoi', label: 'Estimated ROI', tile: buildEstimatedRoiTile(figures) },
  ]
  return tiles.filter((tile): tile is OfferingCardTile => Boolean(tile))
}
